import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import type {
  WikiAdminArcRow,
  WikiAdminChallengeRow,
  WikiAdminPageRow,
  WikiPageStatus,
  WikiPageStatusValidationError,
} from '@fishstank/contracts'
import { WIKI_PAGE_STATUS_TRANSITIONS } from '@fishstank/contracts'
import {
  fetchWikiAdminArcs,
  fetchWikiAdminChallenges,
  fetchWikiAdminList,
  patchWikiEntityPageStatus,
} from '../lib/worker'
import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'

// crypto.randomUUID is widely available in modern browsers + jsdom;
// wrap it so a shim could replace it in the test environment.
function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

// Slice 2 CP5 Phase 3 — minimal admin read surface for wiki pages.
// Slice 2 CP10 Phase 3 — extended with edit affordances:
//   • "+ New entity wiki page" link (→ /wiki/new)
//   • per-row "Edit" Link on the WikiTable (→ /wiki/:id/edit)
//   • Arcs section with per-row "Edit" Link (→ /wiki/arcs/:id/edit)
//   • Challenges section with per-row "Edit" Link (→ /wiki/challenges/:id/edit)
//
// Read sources stay in a single surface (this page) so admin operators
// see one "wiki" index. Each admin-list fetch is independent so a
// failure in one section doesn't blank the others.

export function WikiPage() {
  const listQuery = useQuery({
    queryKey: ['wiki', 'admin', 'list'],
    queryFn: fetchWikiAdminList,
  })
  const arcsQuery = useQuery({
    queryKey: ['wiki', 'admin', 'arcs'],
    queryFn: fetchWikiAdminArcs,
  })
  const challengesQuery = useQuery({
    queryKey: ['wiki', 'admin', 'challenges'],
    queryFn: fetchWikiAdminChallenges,
  })

  const listResult = listQuery.data
  const provenance =
    listResult && (listResult.kind === 'ok' || listResult.kind === 'empty')
      ? listResult.artifact.provenance
      : null

  return (
    <Shell provenance={provenance}>
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">Wiki</h1>
          <p className="mt-1 text-sm text-slate-400">
            Every <code className="text-slate-300">wiki_pages</code> row in
            staging, plus arcs and challenges with editable{' '}
            <code className="text-slate-300">summary_md</code>. Most-recently-
            updated first.
          </p>
        </div>
        <Link
          to="/wiki/new"
          data-testid="wiki-create-open"
          className="rounded-md bg-fishstank-accent px-3 py-1.5 text-sm font-semibold text-fishstank-ink hover:brightness-110"
        >
          + New entity wiki page
        </Link>
      </header>

      <section className="mb-8">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-400">
          Entity wiki pages
        </h2>
        <FetchedArtifact
          result={listResult}
          isLoading={listQuery.isLoading}
          testidPrefix="wiki"
          labels={{
            loading: 'Loading wiki pages…',
            errorTitle: "Couldn't load wiki pages.",
            empty: 'No wiki pages yet.',
          }}
        >
          {(artifact) => (
            <>
              <SummaryBar
                total={artifact.body.wiki_pages.length}
                truncated={artifact.body.truncated}
              />
              <WikiTable pages={artifact.body.wiki_pages} />
            </>
          )}
        </FetchedArtifact>
      </section>

      <section className="mb-8">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-400">
          Arcs
        </h2>
        <FetchedArtifact
          result={arcsQuery.data}
          isLoading={arcsQuery.isLoading}
          testidPrefix="wiki-arcs"
          labels={{
            loading: 'Loading arcs…',
            errorTitle: "Couldn't load arcs.",
            empty: 'No arcs yet.',
          }}
        >
          {(artifact) => (
            <ArcsTable
              arcs={artifact.body.arcs}
              truncated={artifact.body.truncated}
            />
          )}
        </FetchedArtifact>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-400">
          Challenges
        </h2>
        <FetchedArtifact
          result={challengesQuery.data}
          isLoading={challengesQuery.isLoading}
          testidPrefix="wiki-challenges"
          labels={{
            loading: 'Loading challenges…',
            errorTitle: "Couldn't load challenges.",
            empty: 'No challenges yet.',
          }}
        >
          {(artifact) => (
            <ChallengesTable
              challenges={artifact.body.challenges}
              truncated={artifact.body.truncated}
            />
          )}
        </FetchedArtifact>
      </section>
    </Shell>
  )
}

function SummaryBar({
  total,
  truncated,
}: {
  total: number
  truncated: boolean
}) {
  return (
    <div
      data-testid="wiki-summary"
      data-total={total}
      data-truncated={truncated ? 'true' : 'false'}
      className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md border border-slate-800 bg-fishstank-surface px-4 py-3 text-sm"
    >
      <span className="text-slate-400">Showing</span>
      <span className="font-semibold text-slate-100">
        {total} page{total === 1 ? '' : 's'}
      </span>
      {truncated && (
        <span
          data-testid="wiki-truncated-badge"
          className="ml-auto rounded-sm border border-amber-700 bg-amber-950/40 px-2 py-0.5 text-[11px] uppercase tracking-wider text-amber-200"
          title="The wiki_pages table has more rows than the admin cap; tighten the cap or add pagination."
        >
          truncated
        </span>
      )}
    </div>
  )
}

function WikiTable({ pages }: { pages: readonly WikiAdminPageRow[] }) {
  const queryClient = useQueryClient()
  const onChanged = () => {
    void queryClient.invalidateQueries({ queryKey: ['wiki', 'admin', 'list'] })
  }
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="wiki-table"
        data-page-count={pages.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-16">ID</th>
            <th className="px-3 py-2 text-left font-medium w-56">Slug</th>
            <th className="px-3 py-2 text-left font-medium">Title</th>
            <th className="px-3 py-2 text-left font-medium w-56">Status</th>
            <th className="px-3 py-2 text-left font-medium w-24">State</th>
            <th className="px-3 py-2 text-left font-medium w-44">Entity</th>
            <th className="px-3 py-2 text-left font-medium w-44">Updated</th>
            <th className="px-3 py-2 text-left font-medium w-20" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {pages.map((page) => (
            <WikiRow key={page.id} page={page} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WikiRow({
  page,
  onChanged,
}: {
  page: WikiAdminPageRow
  onChanged: () => void
}) {
  return (
    <tr
      data-testid="wiki-row"
      data-page-id={page.id}
      data-page-slug={page.slug}
      data-page-status={page.status}
      data-is-developing={page.is_developing ? 'true' : 'false'}
      data-entity-slug={page.entity_slug ?? ''}
      className="hover:bg-slate-900/40"
    >
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {page.id}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-slate-300">
        {page.slug}
      </td>
      <td className="px-3 py-1.5 text-slate-100">{page.title}</td>
      <td className="px-3 py-1.5 align-top">
        <WikiStatusCell page={page} onChanged={onChanged} />
      </td>
      <td className="px-3 py-1.5">
        {page.is_developing ? (
          <span
            data-testid="wiki-row-developing"
            className="rounded-sm border border-amber-700 bg-amber-950/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-200"
            title="DB-authoritative: status != published OR body_md too short."
          >
            developing
          </span>
        ) : (
          <span className="rounded-sm border border-emerald-800 bg-emerald-950/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-200">
            ready
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs">
        {page.entity_slug ? (
          <span className="font-mono text-slate-300">{page.entity_slug}</span>
        ) : (
          <span
            className="text-slate-600"
            title="No entity_id on this wiki page OR the entity is soft-merged."
          >
            —
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {page.updated_at ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5 text-right">
        <Link
          to={`/wiki/${page.id}/edit`}
          data-testid="wiki-edit-open"
          data-page-id={page.id}
          className="rounded-sm border border-slate-700 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800"
        >
          Edit
        </Link>
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Arcs table (CP10 Phase 3)
// ────────────────────────────────────────────────────────────────────────────

function ArcsTable({
  arcs,
  truncated,
}: {
  arcs: readonly WikiAdminArcRow[]
  truncated: boolean
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="wiki-arcs-table"
        data-arc-count={arcs.length}
        data-truncated={truncated ? 'true' : 'false'}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-16">ID</th>
            <th className="px-3 py-2 text-left font-medium w-40">Slug</th>
            <th className="px-3 py-2 text-left font-medium">Title</th>
            <th className="px-3 py-2 text-left font-medium w-24">Status</th>
            <th className="px-3 py-2 text-left font-medium">Summary</th>
            <th className="px-3 py-2 text-left font-medium w-44">Updated</th>
            <th className="px-3 py-2 text-left font-medium w-20" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {arcs.map((arc) => (
            <tr
              key={arc.id}
              data-testid="wiki-arcs-row"
              data-arc-id={arc.id}
              data-arc-slug={arc.slug}
              data-arc-status={arc.status}
              className="hover:bg-slate-900/40"
            >
              <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
                {arc.id}
              </td>
              <td className="px-3 py-1.5 font-mono text-xs text-slate-300">
                {arc.slug}
              </td>
              <td className="px-3 py-1.5 text-slate-100">{arc.title}</td>
              <td className="px-3 py-1.5 text-xs text-slate-300 uppercase">
                {arc.status}
              </td>
              <td className="px-3 py-1.5 text-xs text-slate-400">
                {arc.summary_md ? (
                  <span className="line-clamp-1">{arc.summary_md}</span>
                ) : (
                  <span className="text-slate-600">—</span>
                )}
              </td>
              <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
                {arc.updated_at ?? <span className="text-slate-600">—</span>}
              </td>
              <td className="px-3 py-1.5 text-right">
                <Link
                  to={`/wiki/arcs/${arc.id}/edit`}
                  data-testid="wiki-arcs-edit-open"
                  data-arc-id={arc.id}
                  className="rounded-sm border border-slate-700 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800"
                >
                  Edit summary
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Challenges table (CP10 Phase 3)
// ────────────────────────────────────────────────────────────────────────────

function ChallengesTable({
  challenges,
  truncated,
}: {
  challenges: readonly WikiAdminChallengeRow[]
  truncated: boolean
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="wiki-challenges-table"
        data-challenge-count={challenges.length}
        data-truncated={truncated ? 'true' : 'false'}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-16">ID</th>
            <th className="px-3 py-2 text-left font-medium w-32">Arc</th>
            <th className="px-3 py-2 text-left font-medium">Title</th>
            <th className="px-3 py-2 text-left font-medium">Summary</th>
            <th className="px-3 py-2 text-left font-medium w-44">Updated</th>
            <th className="px-3 py-2 text-left font-medium w-20" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {challenges.map((ch) => (
            <tr
              key={ch.id}
              data-testid="wiki-challenges-row"
              data-challenge-id={ch.id}
              data-arc-id={ch.arc_id}
              data-arc-slug={ch.arc_slug ?? ''}
              className="hover:bg-slate-900/40"
            >
              <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
                {ch.id}
              </td>
              <td className="px-3 py-1.5 font-mono text-xs text-slate-300">
                {ch.arc_slug ?? (
                  <span className="text-slate-600">—</span>
                )}
              </td>
              <td className="px-3 py-1.5 text-slate-100">{ch.title}</td>
              <td className="px-3 py-1.5 text-xs text-slate-400">
                {ch.summary_md ? (
                  <span className="line-clamp-1">{ch.summary_md}</span>
                ) : (
                  <span className="text-slate-600">—</span>
                )}
              </td>
              <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
                {ch.updated_at ?? <span className="text-slate-600">—</span>}
              </td>
              <td className="px-3 py-1.5 text-right">
                <Link
                  to={`/wiki/challenges/${ch.id}/edit`}
                  data-testid="wiki-challenges-edit-open"
                  data-challenge-id={ch.id}
                  className="rounded-sm border border-slate-700 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800"
                >
                  Edit summary
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Small Tailwind-only status pill. No design-system dependency.
const STATUS_CLASSES: Record<WikiPageStatus, string> = {
  draft: 'border-slate-700 bg-slate-900/60 text-slate-300',
  review: 'border-sky-800 bg-sky-950/40 text-sky-200',
  published: 'border-emerald-800 bg-emerald-950/40 text-emerald-200',
  archived: 'border-slate-800 bg-slate-950/60 text-slate-500',
}

function StatusBadge({ status }: { status: WikiPageStatus }) {
  const cls = STATUS_CLASSES[status] ?? 'border-slate-800 bg-slate-950/60 text-slate-500'
  return (
    <span
      data-testid="wiki-row-status"
      className={`rounded-sm border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}
    >
      {status}
    </span>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// CP12 — minimal inline status controls (entity wiki pages only).
//
// Sits on the existing wiki admin surface (no new dashboard / queue UI).
// Renders the badge plus one button per allowed transition out of the
// current status, calling `patchWikiEntityPageStatus` with a fresh
// idempotency key per click. `archived` flips prompt for an optional
// reason via window.prompt — same minimal approach as the CP11
// review-state controls. Forbidden transitions are not surfaced.
// ────────────────────────────────────────────────────────────────────────────

const STATUS_BUTTON_CLASS: Record<WikiPageStatus, string> = {
  draft:
    'rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30',
  review:
    'rounded-sm border border-sky-700 bg-sky-950/30 text-sky-200 hover:bg-sky-900/40 disabled:opacity-30',
  published:
    'rounded-sm border border-emerald-700 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-30',
  archived:
    'rounded-sm border border-slate-600 bg-slate-900 text-slate-200 hover:bg-slate-800 disabled:opacity-30',
}

const STATUS_BUTTON_LABEL: Record<WikiPageStatus, string> = {
  draft: 'Draft',
  review: 'Review',
  published: 'Publish',
  archived: 'Archive',
}

const STATUS_PROMPTS_FOR_REASON: ReadonlySet<WikiPageStatus> = new Set([
  'archived',
])

function statusValidationMessage(
  err: WikiPageStatusValidationError,
): string {
  switch (err.code) {
    case 'invalid_to_status':
      return `Server rejected to_status="${err.received}".`
    case 'wiki_page_status_unchanged':
      return `Already at ${err.current}.`
    case 'wiki_status_transition_forbidden':
      return `Can't go ${err.from_status} → ${err.to_status} directly.`
    case 'wiki_page_status_idempotency_conflict':
      return `Conflict: that key already flipped to ${err.prior_to_status} (you sent ${err.requested_to_status}). Retry with a fresh key.`
    case 'invalid_uuid':
      return 'Internal error: idempotency key invalid. Refresh and try again.'
    case 'invalid_enum':
      return `Invalid value for "${err.field}".`
    case 'wiki_page_not_found':
      return 'This wiki page no longer exists.'
    default:
      return 'Status flip failed.'
  }
}

function WikiStatusCell({
  page,
  onChanged,
}: {
  page: WikiAdminPageRow
  onChanged: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<WikiPageStatus | null>(null)

  const allowed: readonly WikiPageStatus[] =
    WIKI_PAGE_STATUS_TRANSITIONS[page.status] ?? []

  async function flip(to: WikiPageStatus) {
    if (page.status === to) return
    let reason: string | null = null
    if (STATUS_PROMPTS_FOR_REASON.has(to)) {
      const supplied = window.prompt(
        `Optional reason for flipping wiki page #${page.id} to ${to} (leave blank to skip):`,
        '',
      )
      if (supplied === null) return
      const trimmed = supplied.trim()
      reason = trimmed.length > 0 ? trimmed : null
    }

    setError(null)
    setPending(to)
    try {
      const res = await patchWikiEntityPageStatus(page.id, {
        body: {
          idempotency_key: newIdempotencyKey(),
          to_status: to,
          reason,
        },
      })
      if (res.kind === 'ok') {
        onChanged()
        return
      }
      if (res.kind === 'validation_error') {
        setError(statusValidationMessage(res.error))
        return
      }
      if (res.kind === 'csrf_error') {
        setError(`CSRF check failed: ${res.reason}`)
        return
      }
      if (res.kind === 'not_found') {
        setError('This wiki page no longer exists.')
        return
      }
      setError(res.message)
    } finally {
      setPending(null)
    }
  }

  return (
    <div
      data-testid="wiki-status-cell"
      data-page-id={page.id}
      data-page-status={page.status}
      className="flex flex-col gap-1"
    >
      <StatusBadge status={page.status} />
      <div className="flex flex-wrap gap-1">
        {allowed.map((to) => {
          const disabled = pending !== null
          return (
            <button
              key={to}
              type="button"
              data-testid="wiki-status-button"
              data-page-id={page.id}
              data-to-status={to}
              disabled={disabled}
              onClick={() => {
                void flip(to)
              }}
              className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_BUTTON_CLASS[to]}`}
            >
              {pending === to ? '…' : STATUS_BUTTON_LABEL[to]}
            </button>
          )
        })}
      </div>
      {error && (
        <span
          data-testid="wiki-status-error"
          className="text-[10px] text-red-300"
        >
          {error}
        </span>
      )}
    </div>
  )
}
